import { List, Show, Create, Edit, Datagrid, TextField, ChipField, DateField, SimpleForm, TextInput, SelectInput, Button, Filter, SearchInput, useListContext } from 'react-admin';
import { Box, Typography, Paper, Chip, Stepper, Step, StepLabel, Alert, Grid, Avatar } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import type { WorkflowRecord } from '@/types/entities';
import { Link } from 'react-router-dom';
import {
  PersonRemove, Gavel, TrendingUp, HowToReg, HealthAndSafety, School,
  CheckCircle, Schedule, Warning, ArrowForward, Timeline, Person, Assignment, Security,
  Build, Business, VerifiedUser, CloudUpload, Description, Analytics, AutoStories, Event, Verified, Edit as EditIcon,
  CheckCircleOutline, CancelOutlined,
} from '@mui/icons-material';
import { PlaylistAddCheck } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { BarChart, PieChart } from '@mui/x-charts-pro';

import { STATE_STEPS, WORKFLOW_TYPES, TYPE_LABELS } from './workflowTypeMeta';
const WORKFLOW_STATUS = ['active', 'paused', 'completed', 'cancelled'];

const TYPE_ICONS: Record<string, React.ElementType> = {
  termination: PersonRemove,
  disciplinarywarning: Gavel,
  pip: TrendingUp,
  onboarding: HowToReg,
  ohsincident: HealthAndSafety,
  training: School,
  'contract-alert': Warning,
  'ee-report': Assignment,
  'wsp-atr-report': Description,
  course: AutoStories,
  'course-maintenance': EditIcon,
  trainingprovider: Business,
  'trainingprovider-maintenance': Build,
  'trainingprovider-bulk-import': CloudUpload,
  department: Business,
  'department-maintenance': Build,
  roletemplate: VerifiedUser,
  'roletemplate-maintenance': EditIcon,
  'roletemplate-utilization-review': Analytics,
  'ohs-calendar-event': Event,
  'ohs-certification': Verified,
};

const TYPE_COLORS: Record<string, string> = {
  termination: '#1976d2',
  disciplinarywarning: '#ed6c02',
  pip: '#9c27b0',
  onboarding: '#2e7d32',
  ohsincident: '#00acc1',
  training: '#757575',
  'contract-alert': '#d32f2f',
  'ee-report': '#1565c0',
  'wsp-atr-report': '#4a148c',
  course: '#00838f',
  'course-maintenance': '#607d8b',
  trainingprovider: '#e65100',
  'trainingprovider-maintenance': '#5d4037',
  'trainingprovider-bulk-import': '#37474f',
  department: '#0277bd',
  'department-maintenance': '#78909c',
  roletemplate: '#2e7d32',
  'roletemplate-maintenance': '#558b2f',
  'roletemplate-utilization-review': '#33691e',
  'ohs-calendar-event': '#ad1457',
  'ohs-certification': '#6a1b9a',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'success',
  paused: 'warning',
  completed: 'info',
  cancelled: 'error',
};

export const WorkflowList = () => (
  <List filters={<WorkflowFilters />}>
    <WorkflowListHeader />
    <Datagrid rowClick="show">
      <WorkflowTypeField source="workflowType" />
      <TextField source="entityName" />
      <TextField source="entityId" label="Reference" />
      <WorkflowStateField source="currentState" />
      <ChipField source="status" />
      <DateField source="startedAt" label="Started" />
      <DateField source="lastActionAt" label="Last Action" />
    </Datagrid>
  </List>
);

const WorkflowTypeField = ({ source }: { source: string }) => {
  return <ChipField source={source} label="Type" />;
};

const WorkflowStateField = ({ source }: { source: string }) => {
  return <ChipField source={`${source}.value`} label="State" />;
};

const WorkflowListHeader = () => {
  const { data: workflows } = useListContext();
  const total = workflows?.length ?? 0;
  const active = workflows?.filter((w: WorkflowRecord) => w.status === 'active').length ?? 0;
  const paused = workflows?.filter((w: WorkflowRecord) => w.status === 'paused').length ?? 0;
  const completed = workflows?.filter((w: WorkflowRecord) => w.status === 'completed').length ?? 0;
  const cancelled = workflows?.filter((w: WorkflowRecord) => w.status === 'cancelled').length ?? 0;

  const typeCounts: Record<string, number> = {};
  workflows?.forEach((w: WorkflowRecord) => {
    const t = TYPE_LABELS[w.workflowType] || w.workflowType;
    typeCounts[t] = (typeCounts[t] || 0) + 1;
  });
  const typeData = Object.entries(typeCounts).map(([label, value]) => ({ label, value }));

  return (
    <ListPageHeader
      title="Workflows"
      metrics={[
        { label: 'Total', value: String(total), color: 'primary.main' },
        { label: 'Active', value: String(active), color: 'success.main' },
        { label: 'Paused', value: String(paused), color: 'warning.main' },
        { label: 'Completed', value: String(completed), color: 'info.main' },
        { label: 'Cancelled', value: String(cancelled), color: 'default' },
      ]}
      pieChartData={typeData}
      pieChartTitle="By Type"
      barChartData={[active, paused, completed, cancelled]}
      barChartLabels={['Active', 'Paused', 'Completed', 'Cancelled']}
      barChartTitle="By Status"
    />
  );
};

export const WorkflowFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search by name or reference" />
    <SelectInput source="workflowType" choices={WORKFLOW_TYPES.map(t => ({ id: t, name: TYPE_LABELS[t] }))} />
    <SelectInput source="status" choices={WORKFLOW_STATUS.map(s => ({ id: s, name: s.charAt(0).toUpperCase() + s.slice(1) }))} />
  </Filter>
);

export const WorkflowShow = () => (
  <Show render={({ record }) => {
    const r = record as WorkflowRecord | undefined;
    if (!r) return null;

    const TypeIcon = TYPE_ICONS[r.workflowType] || Assignment;
    const typeColor = TYPE_COLORS[r.workflowType] || '#1976d2';
    const stateSteps = STATE_STEPS[r.workflowType] || [];
    const currentStateIdx = stateSteps.indexOf(r.currentState?.value ?? '');
    const isPaused = r.status === 'paused';
    const isCompleted = r.status === 'completed' || r.status === 'cancelled';

    const transitions = r.transitions || [
      { sequence: 1, event: 'INIT', fromState: null, toState: stateSteps[0] || 'init', timestamp: r.startedAt, triggeredBy: 'system' },
      { sequence: 2, event: 'SUBMIT', fromState: stateSteps[0] || 'init', toState: stateSteps[1] || 'draft', timestamp: '2026-05-16T10:30:00Z', triggeredBy: 'HRManager' },
    ];

    return (
      <Box sx={{ p: 3, mt: 2 }}>
        {isPaused && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={<Schedule />}>
            Workflow paused — awaiting human action. Breakpoint: {r.breakpointId || r.currentState?.value}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: typeColor, width: 48, height: 48 }}>
                  <TypeIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {TYPE_LABELS[r.workflowType] || r.workflowType}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {r.entityName} ({r.entityId})
                  </Typography>
                </Box>
                <Chip
                  label={r.status?.toUpperCase()}
                  color={STATUS_COLORS[r.status] as ChipColor}
                  size="small"
                />
              </Box>

              <Stepper activeStep={Math.max(0, currentStateIdx)} alternativeLabel sx={{ mb: 2 }}>
                {stateSteps.map((step, idx) => {
                  const isDone = idx < currentStateIdx;
                  const isCurrent = idx === currentStateIdx;
                  return (
                    <Step key={step} completed={isDone}>
                      <StepLabel
                        StepIconProps={{
                          style: {
                            color: isDone ? '#2e7d32' : isCurrent ? typeColor : '#bdbdbd',
                          }
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: isCurrent ? 700 : 400, textTransform: 'capitalize' }}>
                          {step.replace(/_/g, ' ')}
                        </Typography>
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Workflow Type', value: TYPE_LABELS[r.workflowType] || r.workflowType },
            { label: 'Current State', value: r.currentState?.value?.replace(/_/g, ' '), chip: true, chipColor: typeColor },
            { label: 'Status', value: r.status, chip: true, chipColor: STATUS_COLORS[r.status] },
            { label: 'Reference', value: r.entityId, bold: true },
          ].map(f => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                {f.chip ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={f.value} size="small" sx={{ bgcolor: f.chipColor, color: 'white' }} />
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5, textTransform: 'capitalize' }}>{f.value ?? '—'}</Typography>
                )}
              </Paper>
            </Grid>
          ))}
          {[
            { label: 'Version', value: r.workflowVersion },
            { label: 'Started', value: r.startedAt },
            { label: 'Last Action', value: r.lastActionAt },
            { label: 'Entity Name', value: r.entityName },
          ].map(f => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>{f.value ?? '—'}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>State Transition History</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {transitions.map((t: NonNullable<WorkflowRecord['transitions']>[number], idx: number) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', bgcolor: idx < transitions.length - 1 ? 'success.main' : 'primary.main', color: 'white' }}>
                      {idx < transitions.length - 1 ? <CheckCircle sx={{ fontSize: 14 }} /> : <Timeline sx={{ fontSize: 14 }} />}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t.event}
                        {t.fromState && ` — ${t.fromState.replace(/_/g, ' ')} → ${t.toState.replace(/_/g, ' ')}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.timestamp} · by {t.triggeredBy}
                      </Typography>
                      {t.ruleResult && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            icon={t.ruleResult.allowed ? <CheckCircleOutline /> : <CancelOutlined />}
                            label={t.ruleResult.allowed ? 'Guards passed' : `Guards failed: ${t.ruleResult.reason || 'restricted'}`}
                            size="small"
                            color={t.ruleResult.allowed ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      )}
                    </Box>
                    <Chip label={`Step ${t.sequence}`} size="small" variant="outlined" />
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 3, my: 3, bgcolor: 'action.hover', borderTop: 4, borderTopColor: 'primary.main', borderLeft: 0, borderRight: 0, borderBottom: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PlaylistAddCheck fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Process Actions</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {!isCompleted && r.currentState?.value === stateSteps[0] && (
              <Button variant="contained" color="success" size="small" startIcon={<CheckCircle />}>
                Submit
              </Button>
            )}
            {!isCompleted && r.currentState?.value === stateSteps[1] && (
              <Button variant="contained" size="small" startIcon={<Schedule />}>
                Advance
              </Button>
            )}
            {!isCompleted && r.currentState?.value === stateSteps[stateSteps.length - 2] && (
              <Button variant="contained" color="success" size="small" startIcon={<CheckCircle />}>
                Complete
              </Button>
            )}
            {isPaused && (
              <Button variant="contained" color="warning" size="small" startIcon={<ArrowForward />}>
                Resume
              </Button>
            )}
            {!isCompleted && (
              <Button variant="outlined" color="error" size="small" startIcon={<Warning />}>
                Cancel
              </Button>
            )}
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {r.workflowType === 'termination' && (
            <Button component={Link} to="/terminations" variant="outlined" size="small" startIcon={<PersonRemove />}>
              View Termination
            </Button>
          )}
          {r.workflowType === 'disciplinarywarning' && (
            <Button component={Link} to="/disciplinary-cases" variant="outlined" size="small" startIcon={<Gavel />}>
              View Disciplinary Case
            </Button>
          )}
          {r.workflowType === 'pip' && (
            <Button component={Link} to="/pips" variant="outlined" size="small" startIcon={<TrendingUp />}>
              View PIP
            </Button>
          )}
          {r.workflowType === 'onboarding' && (
            <Button component={Link} to="/onboarding-processes" variant="outlined" size="small" startIcon={<HowToReg />}>
              View Onboarding
            </Button>
          )}
          {r.workflowType === 'training' && (
            <Button component={Link} to="/training-records" variant="outlined" size="small" startIcon={<School />}>
              View Training
            </Button>
          )}
          {r.workflowType === 'ohsincident' && (
            <Button component={Link} to="/workplace-injuries" variant="outlined" size="small" startIcon={<HealthAndSafety />}>
              View Incident
            </Button>
          )}
          {r.workflowType === 'ee-report' && (
            <Button component={Link} to="/eeReports" variant="outlined" size="small" startIcon={<Assignment />}>
              View EE Report
            </Button>
          )}
          {r.workflowType === 'wsp-atr-report' && (
            <Button component={Link} to="/wspatrReports" variant="outlined" size="small" startIcon={<Description />}>
              View WSP/ATR Report
            </Button>
          )}
          {r.workflowType === 'trainingprovider' && (
            <Button component={Link} to="/trainingProviders" variant="outlined" size="small" startIcon={<School />}>
              View Provider
            </Button>
          )}
          {r.workflowType === 'department' && (
            <Button component={Link} to="/departments" variant="outlined" size="small" startIcon={<Business />}>
              View Department
            </Button>
          )}
          {r.workflowType === 'roletemplate' && (
            <Button component={Link} to="/roleTemplates" variant="outlined" size="small" startIcon={<VerifiedUser />}>
              View Role Template
            </Button>
          )}
          {r.workflowType === 'course' && (
            <Button component={Link} to="/courses" variant="outlined" size="small" startIcon={<AutoStories />}>
              View Course
            </Button>
          )}
          {r.workflowType === 'ohs-certification' && (
            <Button component={Link} to="/ohs-certifications" variant="outlined" size="small" startIcon={<Verified />}>
              View Certification
            </Button>
          )}
          {r.workflowType === 'ohs-calendar-event' && (
            <Button component={Link} to="/ohsCalendarEvents" variant="outlined" size="small" startIcon={<Event />}>
              View Event
            </Button>
          )}
          <Button component={Link} to="/employees" variant="outlined" size="small" startIcon={<Person />}>
            View Employee
          </Button>
          <Button component={Link} to="/audit-logs" variant="outlined" size="small" startIcon={<Security />}>
            View Audit Log
          </Button>
        </Box>
      </Box>
    );
  }} />
);

export const WorkflowCreate = () => (
  <Create>
    <SimpleForm>
      <Alert severity="info" sx={{ mb: 2 }}>
        Workflows are typically created through domain actions. To create a workflow manually:
      </Alert>
      <SelectInput source="workflowType" choices={WORKFLOW_TYPES.map(t => ({ id: t, name: TYPE_LABELS[t] }))} fullWidth />
      <TextInput source="entityId" label="Entity Reference" fullWidth />
      <TextInput source="entityName" label="Entity Name" fullWidth />
    </SimpleForm>
  </Create>
);

export const WorkflowEdit = () => (
  <Edit>
    <SimpleForm>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Workflow state should be managed through process actions, not direct editing.
      </Alert>
      <TextInput source="entityId" label="Entity Reference" disabled fullWidth />
      <ChipField source="status" />
    </SimpleForm>
  </Edit>
);
